import { useRef, useState, useEffect } from 'react';
import { getTimeFromNow } from 'utils/3catinfo/dateHelper';
import { getUrlFriendly } from 'components/3catinfo/Enllac/RouteResolver';
import { dataResources } from 'utils/constants';
import Icona from 'components/3catinfo/ui/Icona';
import Imatge from 'components/3catinfo/Imatge';
import Enllac from 'components/3catinfo/Enllac';
import Boto from 'components/3catinfo/Boto';
import moment from 'moment';
import clsx from 'clsx';
import styles from './cameraDetallada.module.scss';

const CompartirCamara = (props) => {
    const { refButtonCompartir, nom, urlCompartir } = props;
    const [showCompartir, setShowCompartir] = useState(false);
    const [isCopyingLink, setIsCopyingLink] = useState(false);
    const timerCopyLink = useRef(null);
    const secondFocusRef = useRef(null);

    const iframeCode = `<iframe title="Càmera ${nom}" src="${urlCompartir}" frameborder="0" width="600" height="525"></iframe>`;

    const stylesBtnCopiarEnllac = clsx({
        [styles.btn_copia]: true,
        [styles.copiant]: isCopyingLink
    });

    const handleClickCopyLink = () => {
        if (!isCopyingLink) {
            navigator.clipboard.writeText(iframeCode);
            setIsCopyingLink(true);
            timerCopyLink.current = null;
            timerCopyLink.current = setTimeout(() => {
                setIsCopyingLink(false);
            }, 5000);
        }
    };

    useEffect(() => {
        if (showCompartir) {
            secondFocusRef.current.focus();
        }
    }, [showCompartir]);

    useEffect(() => {
        const refButtonCompartirCurrent = refButtonCompartir?.current;
        refButtonCompartirCurrent?.addEventListener('click', () => setShowCompartir(true));

        return () => {
            refButtonCompartirCurrent?.removeEventListener('click', setShowCompartir);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return showCompartir ? (
        <div className={styles.contentCardCompartir}>
            <Boto
                ref={secondFocusRef}
                icona='tanca'
                className={styles.btnTancaCompartir}
                estil='transparent'
                fons='dark'
                tag='button'
                isIconaBlanc={true}
                mida='24'
                altIcona='Tancar'
                iconaInline={true}
                onClick={() => setShowCompartir(false)}
            />
            <p>Copia aquest codi per afegir-lo a la teva pàgina web:</p>
            <textarea className={styles.textareaCompartir} value={iframeCode} readOnly tabIndex={-1} />
            <div className={styles.contentBotoCompartir}>
                <Boto
                    tag='button'
                    text={isCopyingLink ? 'Codi copiat!' : 'Copia el codi'}
                    estil='ple'
                    fons='fonsContenidorNegre'
                    icona={isCopyingLink ? 'check_no_contorn' : 'enllac'}
                    iconaInline={true}
                    isIconaDreta={false}
                    className={stylesBtnCopiarEnllac}
                    onClick={handleClickCopyLink}
                />
            </div>
        </div>
    ) : (
        <></>
    );
};

const CameraDetallada = (props) => {
    const { idSelected = 0, onClickClose, mostraLinkTitol = false } = props;
    let { items, item } = props;

    // Si només ve un item en singular, el convertim en array
    if (!items && item) {
        items = [item];
    }
    if (items && !Array.isArray(items)) {
        items = [items];
    }

    const refBotoCompartir = useRef(null);
    const initialFocusRef = useRef(null);
    const [camera, setCamera] = useState(items?.[idSelected]);
    const { id, nom, snapshots, temperatura_actual, humitat, data_creacio } = camera || {};
    const dataActualitzacio = data_creacio ? getTimeFromNow(moment(data_creacio * 1000).format('DD/MM/YYYY HH:mm:ss')) : null;
    const fitxer = snapshots?.[1].fitxer;
    const { basePath } = dataResources;

    const getQualitatSenseDades = (qualitat) => String(qualitat ?? '').replace('--', 'sense_dades') || 'sense_dades';
    const qualitat_pm10 = getQualitatSenseDades(camera?.qualitat_pm10);
    const qualitat_no2 = getQualitatSenseDades(camera?.qualitat_no2);
    const qualitat_o3 = getQualitatSenseDades(camera?.qualitat_o3);

    const urlCompartir = `https://www.3cat.cat${getUrlFriendly({
        routePath: 'web_3catinfo_el_temps_embed_camera',
        slug: nom,
        id,
        producte: 'web_noticies'
    })}`;

    useEffect(() => {
        setCamera(items[idSelected]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idSelected]);

    useEffect(() => {
        if (initialFocusRef.current) {
            initialFocusRef.current.focus();
        }
    }, []);

    return (
        <div className={styles.cameraWrapper}>
            <div className={styles.contentImatge}>
                {fitxer && (
                    <div className={styles.imatgeWrapper}>
                        <Imatge
                            className={styles.imatge}
                            src={`https://statics.3cat.cat${fitxer}`}
                            layout='fill'
                            alt={nom}
                            aria-hidden='true'
                            objectFit='cover'
                        />
                    </div>
                )}
                <div className={styles.info}>
                    <div className={styles.dades}>
                        <span className={styles.temperatura}>{temperatura_actual}º</span>
                        <span className={styles.humitat}>{humitat}% </span>
                    </div>
                    <div className={styles.qualitats}>
                        <span className={styles.titleQuality}>Qualitat de l&apos;aire</span>
                        <div className={styles.measurements}>
                            <div className={styles.infoQuality}>
                                <Imatge
                                    className={styles.imatgeQuality}
                                    src={`${basePath}/_resources/imatges/3catinfo/cameres/pm10_${qualitat_pm10}.svg`}
                                    alt={`Qualitat PM10 ${qualitat_pm10.replace('_', ' ')}`}
                                    width={32}
                                    height={32}
                                ></Imatge>
                                <span className={styles.text}>{qualitat_pm10.replace('_', ' ')}</span>
                            </div>
                            <div className={styles.infoQuality}>
                                <Imatge
                                    className={styles.imatgeQuality}
                                    src={`${basePath}/_resources/imatges/3catinfo/cameres/no2_${qualitat_no2}.svg`}
                                    alt={`Icona qualitat NO2 ${qualitat_no2.replace('_', ' ')}`}
                                    width={32}
                                    height={32}
                                ></Imatge>
                                <span className={styles.text}>{qualitat_no2.replace('_', ' ')}</span>
                            </div>
                            <div className={styles.infoQuality}>
                                <Imatge
                                    className={styles.imatgeQuality}
                                    src={`${basePath}/_resources/imatges/3catinfo/cameres/o3_${qualitat_o3}.svg`}
                                    alt={`Icona qualitat O3 ${qualitat_o3.replace('_', ' ')}`}
                                    width={32}
                                    height={32}
                                ></Imatge>
                                <span className={styles.text}>{qualitat_o3.replace('_', ' ')}</span>
                            </div>
                        </div>
                    </div>
                </div>
                {onClickClose && (
                    <button className={styles.tanca} onClick={onClickClose} ref={initialFocusRef}>
                        <Icona icona='tanca' isBlanc={true} alt='Tanca' className={styles.tancar} />
                    </button>
                )}
            </div>
            <div className={styles.contentFooter}>
                <div className={styles.contentTitle}>
                    <div className={styles.title}>
                        {mostraLinkTitol ? (
                            <Enllac
                                route={{
                                    type: 'web_3catinfo_el_temps_camera_detall',
                                    slug: nom,
                                    id: id,
                                    producte: 'web_noticies'
                                }}
                                className={styles.enllacTitol}
                            >
                                {nom}
                            </Enllac>
                        ) : (
                            nom
                        )}
                    </div>
                    {dataActualitzacio && (
                        <div className={styles.contentDate}>
                            <Icona className={styles.ico_clock} icona='rellotge' color='blanc' mida='20' alt='Temps' />
                            <span className={styles.date} suppressHydrationWarning>
                                {dataActualitzacio}
                            </span>
                        </div>
                    )}
                </div>
                <Boto
                    ref={refBotoCompartir}
                    icona='enllac'
                    className={styles.botoCompartir}
                    estil='noEstil'
                    tag='button'
                    isIconaBlanc={false}
                    mida='18'
                    alt='Insereix càmera'
                    iconaInline={true}
                    isIconaDreta={false}
                    text='Insereix càmera'
                />
            </div>
            <CompartirCamara refButtonCompartir={refBotoCompartir} nom={nom} urlCompartir={urlCompartir} />
        </div>
    );
};

export default CameraDetallada;
